import { Tabs } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

export const PERFORMANCE_TAB = "performance"
export const COMPARISON_TAB = "comparison"
const TEAM_TAB = "team"
const PERFORMANCE_KEYS = ["grain", "period", "role", "type", "sort", "dir"]
const COMPARISON_KEYS = ["cmp_grain", "cmp_to", "cmp_members", "cmp_metric"]

interface Props {
  ourTeam: ReactNode
  performance: ReactNode
  comparison?: ReactNode
  canSeePerformance: boolean
}

export function TeamManagementTabs({
  ourTeam,
  performance,
  comparison,
  canSeePerformance
}: Props) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get("tab")
  const active =
    canSeePerformance && requested === PERFORMANCE_TAB
      ? PERFORMANCE_TAB
      : canSeePerformance && comparison && requested === COMPARISON_TAB
        ? COMPARISON_TAB
        : TEAM_TAB

  if (!canSeePerformance) return <>{ourTeam}</>

  const onChange = (key: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (key === TEAM_TAB) {
        // Leaving the performance tabs drops their filters; Our Team's own keys stay.
        for (const k of ["tab", ...PERFORMANCE_KEYS, ...COMPARISON_KEYS])
          next.delete(k)
      } else {
        next.set("tab", key)
      }
      return next
    })

  return (
    <Tabs
      activeKey={active}
      onChange={onChange}
      destroyOnHidden
      items={[
        {
          key: TEAM_TAB,
          label: t("teamPerformance.tabs.ourTeam", "Our Team"),
          children: ourTeam
        },
        {
          key: PERFORMANCE_TAB,
          label: t(
            "teamPerformance.tabs.performance",
            "Team Members Performance"
          ),
          children: performance
        },
        ...(comparison
          ? [
              {
                key: COMPARISON_TAB,
                label: t("teamPerformance.tabs.comparison", "Comparison"),
                children: comparison
              }
            ]
          : [])
      ]}
    />
  )
}
